/**
 * Typed calls for every invoicing route of Phase 1 (CONTRACT.md §4). Shapes
 * come from invoicing.types.ts, generated from docs/invoicing/openapi.json —
 * regenerate with `npm run gen:invoicing-types` when the contract changes.
 */
import { authedPosApi } from './authedPosApi';
import type { components } from './invoicing.types';

type S = components['schemas'];
export type InvoiceDoc = S['InvoiceDoc'];
export type InvoiceListItem = S['InvoiceListItem'];
export type DocumentInput = S['DocumentInput'];
export type LineInput = S['LineInput'];
export type Line = S['Line'];
export type Charge = S['Charge'];
export type Discount = S['Discount'];
export type Payment = S['Payment'];
export type InvoiceEvent = S['InvoiceEvent'];
export type InvoiceSettings = S['InvoiceSettings'];
export type DisplayStatus = S['DisplayStatus'];
export type PaymentMethod = S['PaymentMethod'];
export type PaymentTerms = S['PaymentTerms'];
export type CustomerSnapshot = S['CustomerSnapshot'];

export interface ListSummary {
  outstandingMinor: number;
  overdueMinor: number;
  overdueCount: number;
  collectedThisMonthMinor: number;
  averageDaysToPay: number | null;
  pendingApprovalCount: number;
  pendingApprovalMinor: number;
}
export interface ListResult {
  items: InvoiceListItem[];
  nextCursor: string | null;
  counts: Record<string, number>;
  summary: ListSummary;
}
export interface ListQuery {
  docType?: 'INVOICE' | 'QUOTATION';
  tab?: string;
  clientId?: string;
  from?: string;
  to?: string;
  dueFrom?: string;
  dueTo?: string;
  minTotalMinor?: number;
  maxTotalMinor?: number;
  createdBy?: string;
  q?: string;
  limit?: number;
  cursor?: string;
}
export interface RelatedDoc { docId: string; docType: string; number: string | null; totalMinor: number; shareUrl?: string }
export interface DocumentDetail {
  document: InvoiceDoc;
  payments: Payment[];
  events: InvoiceEvent[];
  messages: unknown[];
  related: { parent?: RelatedDoc; children: RelatedDoc[] };
}
export interface PaymentInput {
  amountMinor: number;
  paidOn: string;
  method: PaymentMethod;
  reference?: string;
  chequeNo?: string;
  chequeBank?: string;
  note?: string;
}
export type CreditNoteInput =
  | { reason: string; reasonCode?: string; lines: { lineId: string; quantity: number }[] }
  | { reason: string; reasonCode?: string; amountMinor: number };

export interface ShareMessage {
  text: string;
  subject?: string;
  link: string;
  templateKey: string;
  language: 'en' | 'si' | 'ta';
  sms?: { encoding: 'GSM7' | 'UCS2'; characters: number; segments: number };
  to: { phoneE164?: string; email?: string };
}
export type ManualChannel = 'MANUAL_WHATSAPP' | 'MANUAL_SMS' | 'MANUAL_EMAIL' | 'COPY_LINK' | 'COPY_MESSAGE' | 'QR_DOWNLOAD';
export type PendingClaim = Payment & { slipUrl?: string; payerName?: string; possibleDuplicate?: boolean; document: InvoiceListItem & { balanceMinor: number } };
export type RejectCode = 'NOT_RECEIVED' | 'WRONG_AMOUNT' | 'DUPLICATE' | 'OTHER';
/** Built-in message texts: templateKey → channel → language → text (EMAIL: { subject, text }). */
export type TemplateDefaults = Record<string, Record<string, Record<string, string | { subject: string; text: string }>>>;

const doc = (id: string) => `/invoices/${encodeURIComponent(id)}`;
const newKey = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const InvoiceRepository = {
  list: (shopId: string, query: ListQuery = {}) =>
    authedPosApi<ListResult>(shopId, '/invoices', { query: query as any }),

  /** Fetches every page; used by CSV export. */
  async listAll(shopId: string, query: ListQuery = {}): Promise<InvoiceListItem[]> {
    const items: InvoiceListItem[] = [];
    let cursor: string | undefined;
    do {
      const page = await InvoiceRepository.list(shopId, { ...query, limit: 200, cursor });
      items.push(...page.items);
      cursor = page.nextCursor ?? undefined;
    } while (cursor);
    return items;
  },

  get: (shopId: string, docId: string) => authedPosApi<DocumentDetail>(shopId, doc(docId)),

  create: (shopId: string, input: DocumentInput, idempotencyKey: string = newKey()) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, '/invoices', { method: 'POST', body: input, idempotencyKey }),

  update: (shopId: string, docId: string, input: Partial<DocumentInput>) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, doc(docId), { method: 'PUT', body: input }),

  remove: (shopId: string, docId: string) => authedPosApi<null>(shopId, doc(docId), { method: 'DELETE' }),

  issue: (shopId: string, docId: string) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, `${doc(docId)}/issue`, { method: 'POST', body: {} }),

  void: (shopId: string, docId: string, reason: string) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, `${doc(docId)}/void`, { method: 'POST', body: { reason } }),

  duplicate: (shopId: string, docId: string, docType?: 'INVOICE' | 'QUOTATION') =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, `${doc(docId)}/duplicate`, { method: 'POST', body: docType ? { docType } : {} }),

  creditNote: (shopId: string, docId: string, input: CreditNoteInput) =>
    authedPosApi<{ creditNote: InvoiceDoc; document: InvoiceDoc }>(shopId, `${doc(docId)}/credit-notes`, { method: 'POST', body: input }),

  recordPayment: (shopId: string, docId: string, input: PaymentInput, idempotencyKey: string = newKey()) =>
    authedPosApi<{ payment: Payment; document: InvoiceDoc; receipt: InvoiceDoc }>(shopId, `${doc(docId)}/payments`, { method: 'POST', body: input, idempotencyKey }),

  pdfUrl: (shopId: string, docId: string, size: 'A4' | '80mm' = 'A4', copy: 'ORIGINAL' | 'DUPLICATE' = 'ORIGINAL') =>
    authedPosApi<{ url: string; expiresAt: number }>(shopId, `${doc(docId)}/pdf`, { query: { size, copy } }),

  regenerateLink: (shopId: string, docId: string) =>
    authedPosApi<{ shareCode: string; shareUrl: string }>(shopId, `${doc(docId)}/share-link/regenerate`, { method: 'POST', body: {} }),

  setRemindersPaused: (shopId: string, docId: string, paused: boolean) =>
    authedPosApi<{ document: InvoiceDoc }>(shopId, `${doc(docId)}/reminders`, { method: 'POST', body: { paused } }),

  shareMessage: (shopId: string, docId: string, query: { channel?: string; language?: string; templateKey?: string } = {}) =>
    authedPosApi<ShareMessage>(shopId, `${doc(docId)}/share-message`, { query }),

  shareEvent: (shopId: string, docId: string, channel: ManualChannel) =>
    authedPosApi<{ event: InvoiceEvent }>(shopId, `${doc(docId)}/share-events`, { method: 'POST', body: { channel } }),

  pendingClaims: (shopId: string, query: { method?: string; clientId?: string } = {}) =>
    authedPosApi<{ items: PendingClaim[]; count: number; totalMinor: number }>(shopId, '/invoices/payments/pending', { query }),

  approveClaim: (shopId: string, paymentId: string, amountMinor?: number) =>
    authedPosApi<{ payment: Payment; document: InvoiceDoc; receipt: InvoiceDoc }>(shopId, `/invoices/payments/${encodeURIComponent(paymentId)}/approve`, { method: 'POST', body: amountMinor ? { amountMinor } : {} }),

  rejectClaim: (shopId: string, paymentId: string, reasonCode: RejectCode, reason?: string) =>
    authedPosApi<{ payment: Payment; document: InvoiceDoc }>(shopId, `/invoices/payments/${encodeURIComponent(paymentId)}/reject`, { method: 'POST', body: { reasonCode, reason } }),

  approveBulk: (shopId: string, paymentIds: string[]) =>
    authedPosApi<{ approved: { paymentId: string; docId: string }[]; failed: { paymentId: string; code: string }[] }>(shopId, '/invoices/payments/approve-bulk', { method: 'POST', body: { paymentIds } }),

  getSettings: (shopId: string) => authedPosApi<{ settings: InvoiceSettings & { templateDefaults?: TemplateDefaults } }>(shopId, '/invoice-settings'),

  saveSettings: (shopId: string, patch: Partial<InvoiceSettings> & Record<string, unknown>) =>
    authedPosApi<{ settings: InvoiceSettings }>(shopId, '/invoice-settings', { method: 'PUT', body: patch }),
};

export default InvoiceRepository;
